import React, { Suspense, lazy } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';

// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
      textAlign: 'center',
      minHeight: 200
    }}
  >
    <Typography variant="h6" color="error" gutterBottom>
      Something went wrong
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {error.message}
    </Typography>
    <Button variant="outlined" onClick={resetErrorBoundary}>
      Try again
    </Button>
  </Box>
);

// Loading fallback component
const LoadingFallback = ({ message = "Loading..." }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
      minHeight: 200
    }}
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      {message}
    </Typography>
  </Box>
);

// Lazy load components with error boundaries
export const LazyStatisticsPage = lazy(() => 
  import('../../pages/StatisticsPage').then(module => ({ default: module.default }))
);

export const LazyRecordsPage = lazy(() => 
  import('../../pages/RecordsPage').then(module => ({ default: module.default }))
);

export const LazySubmitMisconductCase = lazy(() => 
  import('../../pages/SubmitMisconductCase').then(module => ({ default: module.default }))
);

export const LazyJusticeMapPage = lazy(() =>
  import('../../pages/JusticeMapPage').then(module => ({ default: module.default }))
);

// HOC for wrapping components with error boundary and suspense
export const withLazyLoading = (Component, loadingMessage) => {
  return React.forwardRef((props, ref) => (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.reload()}
    >
      <Suspense fallback={<LoadingFallback message={loadingMessage} />}>
        <Component {...props} ref={ref} />
      </Suspense>
    </ErrorBoundary>
  ));
};

// Preload components on hover or interaction
export const useComponentPreloader = () => {
  const preloadComponent = (importFunction) => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => importFunction());
    } else {
      setTimeout(() => importFunction(), 100);
    }
  };

  return { preloadComponent };
};
